import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import DELAY from 'constants/delay.const';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import delayed from 'utils/functions/delayed.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';

import { DownloadExcelButton } from 'reports/Common/DownloadExcelButton';

import CN from './CountOfServicesByCategoryTable.module.scss';

const CountOfServicesByCategoryTable = ({ stylistPerformedCategoriesReport, startDate, endDate }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const TABLE_HEADERS = useMemo(() => [t('businessReports.services.staffHeader'), t('businessReports.services.totalServicesHeader')], [t]);

  const [categoryArr, setCategoryArr] = useState([]);
  const [uniqCagegoryNames, setUniqCagegoryNames] = useState([]);

  const sortArrayAccordingToProp = (arr) => arr.sort((a, b) => (b.total_count > a.total_count ? 1 : -1));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const makeCategoryTable = (data) => {
    const unsortedata = data.slice();
    const newData = sortArrayAccordingToProp(unsortedata);
    const categoryArr = [];
    const allCategorieNames = [];

    if (newData.length > 0) {
      for (let i = 0; i < newData.length; i += 1) {
        const categoryObj = {
          stylist: newData[i].name,
          count: newData[i].total_count,
        };
        const catAsArray = Object.values(newData[i].categories);
        if (catAsArray && catAsArray.length > 0) {
          for (let j = 0; j < catAsArray.length; j += 1) {
            const categoryName = catAsArray[j].name;
            allCategorieNames.push(categoryName);
            categoryObj[categoryName] = catAsArray[j].count;
          }
        }
        categoryArr.push(categoryObj);
      }
    }

    const uniqCagegoryNames = [...new Set(allCategorieNames)];
    const total = {
      stylist: 'Salon total',
      count: 0,
    };
    for (let j = 0; j < categoryArr.length; j += 1) {
      total.count += categoryArr[j].count;
    }
    for (let i = 0; i < uniqCagegoryNames.length; i += 1) {
      const catname = uniqCagegoryNames[i];
      total[catname] = 0;
      for (let j = 0; j < categoryArr.length; j += 1) {
        total[catname] += categoryArr[j][catname] ? categoryArr[j][catname] : 0;
      }
    }
    if (!categoryArr.includes(total)) {
      categoryArr.push(total);
    }

    setCategoryArr(categoryArr);
    setUniqCagegoryNames(uniqCagegoryNames);
  };

  useEffect(() => {
    if (stylistPerformedCategoriesReport && categoryArr.length === 0) {
      delayed(DELAY.flash, () => makeCategoryTable(stylistPerformedCategoriesReport));
    }
  }, [stylistPerformedCategoriesReport, categoryArr.length, makeCategoryTable]);

  const tableHeader = (
    <tr>
      <th className={CN.firstCell}>{TABLE_HEADERS[0]}</th>
      <th>
        <div className={CN.tableHeaderText}>
          <div className={CN.labelSpace}>{TABLE_HEADERS[1]}</div>
          <Tooltip content={t('businessReports.services.totalServicesTooltip')} variant="quaternaryBottom" width="max">
            <div className={CN.iconTooltip}>
              <Icon variant="info" width="sm" />
            </div>
          </Tooltip>
        </div>
      </th>
      {uniqCagegoryNames?.map?.((name, index) => (
        <th key={createNodeKey({ tag: `${name}--${index}`, unique: name, index })}>{name}</th>
      ))}
      <th className={CN.lastCell}></th>
    </tr>
  );

  const createCsv = useCallback(() => {
    const headers = [...TABLE_HEADERS, ...uniqCagegoryNames];

    return asArray(categoryArr).map((category) =>
      headers.reduce((acc, header) => {
        if (header === TABLE_HEADERS[0]) {
          acc[header] = category.stylist || '';
        } else if (header === TABLE_HEADERS[1]) {
          acc[header] = category.count || 0;
        } else {
          acc[header] = category[header] || 0;
        }
        return acc;
      }, {}),
    );
  }, [TABLE_HEADERS, uniqCagegoryNames, categoryArr]);

  const hasTableData = fromAnyToBool(categoryArr.length);

  return (
    <div
      data-bem="CountOfServicesByCategoryTable"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>{t('businessReports.services.servicesByCategoryTableTitle')}</div>
        <DownloadExcelButton getCsvData={createCsv} fileName={t('businessReports.services.servicesByCategoryFileName')} disabled={!hasTableData} />
      </div>
      <div className={CN.timeContainer}>
        <div>
          <Space>{t('businessReports.services.fromDate')}</Space>
          <span className={CN.bold}>
            <Space>{startDate}</Space>
          </span>
        </div>
        <div>
          <Space>{t('businessReports.services.toDate')}</Space>
          <span className={CN.bold}>{endDate}</span>
        </div>
      </div>
      <div className={CN.scrollContainer}>
        <table className={CN.table} id="CountOfServicesByCategoryTable">
          <thead>{tableHeader}</thead>
          <tbody>
            {categoryArr?.map((cat, index) => (
              <tr key={createNodeKey({ index, tag: cat.stylist })}>
                <td className={CN.firstCell}>{truncateString({ value: cat.stylist, max: 24, suffix: '...' })}</td>
                <td>{cat['count']}</td>
                {uniqCagegoryNames?.map?.((name, index) => (
                  <td key={createNodeKey({ index, tag: `${name}--${index}` })}>{cat[name] ? cat[name] : 0}</td>
                ))}
                <td className={CN.lastCell} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CountOfServicesByCategoryTable;
